import { memo, useMemo, useEffect, useState, useCallback } from 'react';
import { useProject, useProjectDone, useProjectIdDetailPartCode } from 'hooks/useProject';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Box, Container, Skeleton, Stack } from '@mui/material';
import { persistor, useAppDispatch } from 'store';
import { openDialog } from 'store/dialog/dialogSlice';
import NotFoundProjectIdDialog from 'components/inspection/NotFoundProjectIdDialog';
import useInspection from 'hooks/useInspection';
import PositiveButton from 'components/cmn/PositiveButton';
import GuideCard from 'components/inspection/GuideCard';
import GuideText from 'components/inspection/GuideText';
import GuideAgreement from 'components/inspection/GuideAgreement';
import { resetOtherOption } from 'store/project/projectSlice';
import { reset } from 'store/inspection/inspectionSlice';
import { IProjectDoneProps } from 'interface/project/projectInfo';
import TutorialArcade from 'components/inspection/TutorialArcade';

function ParticipationPage(): JSX.Element {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { changeSurveyAdd, changeInspectionDetail, inspectionDetail: info } = useInspection();
  const { projectId, participationCode } = useParams();
  const { changeUrlProjectId, changeUrlPartCode, changeProjectName } = useProject();
  const { data, isError, isLoading } = useProjectIdDetailPartCode(projectId || '', participationCode || '');
  const { data: doneData, isLoading: isDoneLoading } = useProjectDone(projectId || '', participationCode || '');

  // 동적 높이 계산을 위한 상태
  const [windowHeight, setWindowHeight] = useState(window.innerHeight);

  // 윈도우 리사이즈 감지
  useEffect(() => {
    const handleResize = () => {
      setWindowHeight(window.innerHeight);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 초기화: 로컬스토리지와 쿠키 삭제
  useEffect(() => {
    // 로컬스토리지 초기화
    localStorage.clear();

    // 쿠키 초기화
    document.cookie.split(';').forEach(cookie => {
      const eqPos = cookie.indexOf('=');
      const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
    });
  }, []);

  const projectDetailInfo = useMemo(() => {
    return data || {};
  }, [data]);
  const surveyInfo = useMemo(() => projectDetailInfo?.survey?.questions || [], [projectDetailInfo?.survey?.questions]);
  const surveyAddInfo = useMemo(
    () => projectDetailInfo?.surveyAdd?.questions || [],
    [projectDetailInfo?.surveyAdd?.questions],
  );
  const projectDoneInfo = useMemo(() => {
    return doneData || {};
  }, [doneData]);

  useEffect(() => {
    async function handleInitAction() {
      try {
        await persistor.persist();
        await dispatch(resetOtherOption());
        await dispatch(reset());
      } catch (e) {
        console.log(e);
      }
    }
    handleInitAction();
  }, [dispatch]);

  const handleCheckCompletionStatus = useCallback((checkData: IProjectDoneProps): boolean => {
    const { isDoneSurvey, isDoneInspection } = checkData;
    if (isDoneSurvey === 'Y' && isDoneInspection === null) {
      return true;
    }

    if (isDoneSurvey === null && isDoneInspection === 'Y') {
      return true;
    }

    if (isDoneSurvey === 'Y' && isDoneInspection === 'Y') {
      return true;
    }
    return false;
  }, []);

  useEffect(() => {
    if (!isLoading && typeof isError === 'object' && isError?.response?.status !== 200) {
      dispatch(
        openDialog({
          content: <NotFoundProjectIdDialog errorInfo={isError} />,
          disableBackdropClick: true,
          disableEscapeKeyDown: true,
        }),
      );
    } else if (projectId && participationCode) {
      changeUrlProjectId(projectId || '');
      changeUrlPartCode(participationCode || '');
    }
  }, [
    changeUrlPartCode,
    changeUrlProjectId,
    dispatch,
    isError,
    isLoading,
    navigate,
    participationCode,
    projectId,
    surveyInfo.length,
  ]);

  useEffect(() => {
    changeInspectionDetail(
      projectDetailInfo?.inspection
        ? {
            ...projectDetailInfo.inspection,
            inspectionName: projectDetailInfo?.compName || '',
            inspectionDesc: projectDetailInfo?.projectName || '',
          }
        : {
            id: '',
            inspectionName: '',
            inspectionDesc: '',
            inspectionFilename: '',
            grid: '',
            sort: 0,
            inspectionIpsatives: [],
            saveFileName: '',
            regId: '',
            fileUrl: '',
          },
    );
  }, [
    changeInspectionDetail,
    projectDetailInfo?.inspection,
    projectDetailInfo?.compName,
    projectDetailInfo?.projectName,
  ]);

  useEffect(() => {
    changeSurveyAdd(surveyAddInfo || []);
  }, [changeSurveyAdd, surveyAddInfo]);

  useEffect(() => {
    changeProjectName(projectDetailInfo.projectName || '');
  }, [changeProjectName, projectDetailInfo.projectName]);

  const [step, setStep] = useState(0);
  const [check, setCheck] = useState({
    agreement: false,
  });
  const handleNext = () => {
    const callbackUrl = searchParams.get('callbackUrl');
    const userId = searchParams.get('userId');

    // callbackUrl과 userId를 모두 포함한 쿼리 스트링 생성
    const queryParams = new URLSearchParams();
    if (callbackUrl) queryParams.append('callbackUrl', callbackUrl);
    if (userId) queryParams.append('userId', userId);
    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';

    if (step === 1 && surveyInfo.length !== 0) {
      navigate(`/mind/survey${queryString}`);
    } else if (step === 2 && !isLoading && surveyInfo.length === 0) {
      navigate(`/mind/classification${queryString}`);
    } else {
      setStep(prev => prev + 1);
    }
  };
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCheck({
      ...check,
      [event.target.name]: event.target.checked,
    });
  };
  const { agreement } = check;
  const isDisabled = useMemo(() => {
    if (step === 0) return isLoading;
    if (step === 1) return !agreement;
    if (step === 2) return false;
    return false;
  }, [agreement, isLoading, step]);

  const buttonTitle = useMemo(() => {
    if (step === 1) return surveyInfo.length === 0 ? '튜토리얼 보기' : '다음';
    if (step === 2) return surveyInfo.length === 0 ? '진단 시작' : '다음';
    return '다음';
  }, [step, surveyInfo.length]);

  const isDiagnosisStart = useMemo(() => {
    return step === 2 && surveyInfo.length === 0;
  }, [step, surveyInfo.length]);

  const loading = isLoading || isDoneLoading;
  const isDone = useMemo(
    () => !isDoneLoading && handleCheckCompletionStatus(projectDoneInfo),
    [handleCheckCompletionStatus, isDoneLoading, projectDoneInfo],
  );

  // 버튼 높이 계산 (모바일: 72px, 데스크탑: 84px)
  const buttonAreaHeight = window.innerWidth < 600 ? 72 : 84;
  // 상단 패딩 여유 공간 추가 (모바일: 20px, 데스크탑: 30px)
  const topPadding = window.innerWidth < 600 ? 20 : 30;
  const contentHeight = windowHeight - buttonAreaHeight - topPadding;

  return (
    <Box
      sx={{
        width: '100%',
        height: `${windowHeight}px`,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          height: `${contentHeight}px`,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'flex-start',
          px: { xs: 2, sm: 4 },
          pt: `${topPadding}px`,
          pb: { xs: 2, sm: 4 },
        }}
      >
        <Container maxWidth="lg" sx={{ height: '100%', display: 'flex', alignItems: 'flex-start', justifyContent: 'center' }}>
          <Stack sx={{ width: '100%', display: 'flex', justifyContent: 'flex-start', marginTop: 'auto', marginBottom: 'auto' }}>
            {!isDone ? (
              <>
                {step === 0 && (
                  <GuideCard
                    isLoading={loading}
                    name={info.inspectionName}
                    desc={info.inspectionDesc}
                    content={<GuideText text={projectDetailInfo?.guide?.des || ''} />}
                  />
                )}
                {step === 1 && (
                  <GuideCard
                    isLoading={loading}
                    name={info.inspectionName}
                    desc={info.inspectionDesc}
                    content={<GuideText text={projectDetailInfo?.guide?.agreement || ''} />}
                    content2={<GuideAgreement agreement={agreement} handleChange={handleChange} />}
                  />
                )}
                {step === 2 && (
                  <GuideCard
                    isLoading={loading}
                    name={info.inspectionName}
                    desc={info.inspectionDesc}
                    content={<TutorialArcade />}
                    customStyle={{
                      xs: 600,
                      sm: 600,
                      maxHeight: 700,
                    }}
                  />
                )}
              </>
            ) : (
              <Stack
                direction="column"
                justifyContent="center"
                alignContent="center"
                alignItems="center"
                sx={{ height: '100%' }}
              >
                <Box typography="s48w900" sx={{ mb: 3 }}>
                  이미 검사를 완료하셨습니다.
                </Box>
              </Stack>
            )}
          </Stack>
        </Container>
      </Box>
      {!isDone && (
        <Box
          sx={{
            height: `${buttonAreaHeight}px`,
            bgcolor: 'background.paper',
            boxShadow: '0px -2px 10px rgba(0, 0, 0, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: { xs: 2, sm: 4 },
            flexShrink: 0,
          }}
        >
          <Stack direction="row" spacing={1.25}>
            {loading ? (
              <Skeleton
                variant="text"
                sx={{
                  width: { xs: 100, sm: 152 },
                  height: { xs: 40, sm: 52 },
                  typography: 's14w500',
                }}
              />
            ) : (
              <PositiveButton
                disabled={isDisabled}
                variant="contained"
                color={isDiagnosisStart ? 'error' : 'primary'}
                onClick={handleNext}
                sx={{
                  width: { xs: 120, sm: 130 },
                  height: { xs: 40, sm: 52 },
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                  borderRadius: 2,
                }}
                title={buttonTitle}
              />
            )}
          </Stack>
        </Box>
      )}
    </Box>
  );
}

export default memo(ParticipationPage);
